import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChevronLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m15 18-6-6 6-6"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m9 18 6-6-6-6"/>
  </svg>
);

const GanttIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18"/><rect width="12" height="3" x="7" y="5" rx="1"/><rect width="9" height="3" x="10" y="10" rx="1"/><rect width="6" height="3" x="13" y="15" rx="1"/>
  </svg>
);

const DashboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/>
  </svg>
);

const UsersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const FolderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/>
  </svg>
);

const FileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/>
  </svg>
);

const ShieldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const LogOutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/>
  </svg>
);

const DashboardLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Default collapsed on mobile
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 768);

  const isAdminSection = location.pathname.startsWith('/admin');

  const mainNavItems = [
    { path: '/app', icon: GanttIcon, label: 'Gantt Chart' },
  ];

  const adminNavItems = [
    { path: '/admin', icon: DashboardIcon, label: 'Dashboard', exact: true },
    { path: '/admin/users', icon: UsersIcon, label: 'Users' },
    { path: '/admin/projects', icon: FolderIcon, label: 'Projects' },
    { path: '/admin/audit', icon: FileIcon, label: 'Audit Logs' },
  ];

  const navItems = isAdminSection ? adminNavItems : mainNavItems;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isActiveRoute = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex overflow-x-hidden" data-testid="dashboard-layout">
      <aside
        className={`fixed left-0 top-0 h-full bg-[#0A0A0B] border-r border-[#27272A] transition-all duration-300 z-40 ${
          sidebarCollapsed ? 'w-14' : 'w-48 md:w-56'
        }`}
        data-testid="sidebar"
      >
        <div className="h-14 flex items-center justify-between px-3 border-b border-[#27272A]">
          {!sidebarCollapsed && (
            <Link to="/app" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/condensys-logo.png" alt="CONDENSYS" className="h-6 object-contain" />
            </Link>
          )}
          {sidebarCollapsed && (
            <Link to="/app" className="mx-auto" data-testid="logo-link-collapsed">
              <img src="/condensys-logo.png" alt="C" className="h-5 object-contain" />
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="text-muted-foreground hover:text-white h-8 w-8"
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </Button>
        </div>

        <nav className="p-2 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActiveRoute(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-2 py-2 rounded-lg transition-all duration-200 group ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-white hover:bg-white/5'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon />
                {!sidebarCollapsed && <span className="font-medium text-sm">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {isAdmin && (
          <div className="absolute bottom-16 left-0 right-0 px-2">
            <Link
              to={isAdminSection ? '/app' : '/admin'}
              className="flex items-center gap-2 px-2 py-2 rounded-lg text-muted-foreground hover:text-white hover:bg-white/5 transition-all duration-200"
              data-testid="switch-view"
            >
              {isAdminSection ? (
                <>
                  <GanttIcon />
                  {!sidebarCollapsed && <span className="font-medium text-sm">Gantt View</span>}
                </>
              ) : (
                <>
                  <ShieldIcon />
                  {!sidebarCollapsed && <span className="font-medium text-sm">Admin Panel</span>}
                </>
              )}
            </Link>
          </div>
        )}
      </aside>

      <div className={`flex-1 ${sidebarCollapsed ? 'ml-14' : 'ml-48 md:ml-56'} transition-all duration-300 min-w-0 max-w-full overflow-x-hidden`}>
        <header className="h-14 bg-[#0A0A0B]/80 backdrop-blur-xl border-b border-[#27272A] sticky top-0 z-40">
          <div className="h-full px-4 md:px-6 flex items-center justify-between max-w-full">
            <div className="flex items-center gap-2">
              <h1 className="text-base md:text-xl font-semibold text-white" data-testid="page-title">
                {isAdminSection ? 'Admin Panel' : 'Timeline'}
              </h1>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-3 hover:bg-white/5 px-2"
                  data-testid="user-menu-trigger"
                >
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                  </div>
                  <Avatar className="w-9 h-9 border-2 border-primary/20">
                    <AvatarImage src={user?.avatar_url ? `${BACKEND_URL}${user.avatar_url}` : undefined} />
                    <AvatarFallback className="bg-primary/20 text-primary text-sm font-semibold">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#121214] border-[#27272A]">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-white">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-[#27272A]" />
                <DropdownMenuItem
                  onClick={() => navigate('/app/profile')}
                  className="cursor-pointer text-muted-foreground hover:text-white focus:text-white focus:bg-white/5"
                  data-testid="profile-menu-item"
                >
                  <UserIcon />
                  <span className="ml-2">Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/app/profile')}
                  className="cursor-pointer text-muted-foreground hover:text-white focus:text-white focus:bg-white/5"
                  data-testid="settings-menu-item"
                >
                  <SettingsIcon />
                  <span className="ml-2">Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#27272A]" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-destructive hover:text-destructive focus:text-destructive focus:bg-destructive/10"
                  data-testid="logout-menu-item"
                >
                  <LogOutIcon />
                  <span className="ml-2">Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-3 md:p-6 min-w-0 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
};

export default DashboardLayout;
